import { useAccount } from 'wagmi'
import { T } from '@/lib/theme'
import { Panel, SectionLabel } from '@/components/ui'
import { TokenGlyph } from '@/components/dashboard/TokenGlyph'
import { SavingsPanel } from '@/components/dashboard/SavingsPanel'
import { BorrowForm } from '@/components/dashboard/markets/ReserveForms'
import { useProtocolState } from '@/hooks/useProtocolState'

// ─────────────────────────────────────────────────────────────────
// The hUSD dollar hub — both halves of Horizon's own dollar on one page.
//
// Save and Mint are two sides of one instrument, so they live together. Both are the SAME wide,
// two-column panel shape (pitch + pills on the left, the action on the darker right), so the page
// reads as one product rather than two unrelated widgets. The savings card leads (the reason to want
// hUSD); minting sits below (how to get some). Both reuse the real components — a curated front
// door onto the same facilitator reserve and savings vault, not a fork of them.
// ─────────────────────────────────────────────────────────────────

export function HUsdSection() {
  const { address: user } = useAccount()
  const { d, refetch } = useProtocolState()
  // The facilitator reserve IS hUSD — the contract is the only thing that knows, so read the flag
  // rather than matching a symbol string.
  const husd = d.reserves?.find((r) => r.isFacilitator) || null

  return (
    <div>
      <div style={hero}>
        <TokenGlyph symbol="hUSD" size={44} />
        <div style={{ minWidth: 0 }}>
          <div style={heroTitle}>hUSD</div>
          <div style={heroSub}>Horizon’s dollar. Hold it and earn, or mint it against your collateral.</div>
        </div>
      </div>

      {!husd && (
        <div style={banner}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: T.accent, flex: '0 0 auto' }} />
          hUSD goes live at launch. Save and mint open once Horizon’s dollar is deployed.
        </div>
      )}

      {/* Earn — the savings card leads. It renders its own SectionLabel + wide two-column panel. */}
      <SavingsPanel />

      {/* Mint — same wide two-column shape as the savings card, so the two halves mirror each other. */}
      <div style={{ marginTop: 44 }}>
        <SectionLabel>Mint hUSD</SectionLabel>
        <div style={intro}>
          Borrow hUSD against your blue-chip collateral. Every unit is minted on demand and burned on
          repay, so the supply only ever reflects real, over-collateralised debt.
        </div>

        {/* Always visible; when hUSD is not deployed yet the pitch stays but the form greys out with an
            "opens at launch" box, mirroring the Mining page rather than hiding the page. */}
        <Panel style={{ padding: 0, overflow: 'hidden', opacity: husd ? 1 : 0.55 }}>
          <div style={grid}>
            <div style={leftCol}>
              <div style={headline}>Borrow the dollar.</div>
              <div style={pitch}>
                Every hUSD is minted the moment you borrow it and burned the moment you repay, so
                it stays backed by collateral you have already posted. Keep your health factor
                comfortable and you hold a dollar you can spend, lend, or save.
              </div>
              <div style={pills}>
                <Pill>Minted on demand</Pill>
                <Pill>Burned on repay</Pill>
                <Pill>Blue-chip collateral</Pill>
              </div>

              <div style={{ marginTop: 30 }}>
                <div style={stratHead}>IF $1 DRIFTS</div>
                <StratCard
                  tone={T.positive} tag="hUSD below $1" title="Buy the discount"
                  body="Buy hUSD under a dollar and repay your debt at face value, or park it in shUSD for the raised savings rate. You keep the spread, and the buying pressure lifts the price back toward $1."
                />
                <StratCard
                  tone={T.purple} tag="hUSD above $1" title="Mint the premium"
                  body="Mint fresh hUSD against spare collateral and sell it above a dollar. You bank the premium, and the added supply settles price back toward $1."
                />
                <StratCard
                  tone={T.cyan} tag="hUSD at $1" title="Simply earn"
                  body="Hold hUSD in the savings account to collect the spread-funded rate plus the HORIZON bonus while it holds at $1."
                />
              </div>
            </div>
            <div style={rightCol}>
              {husd
                ? <BorrowForm reserve={husd} d={d} user={user} onTx={refetch} bare />
                : <NotLiveBox note="Minting opens at launch, once hUSD is deployed." />}
            </div>
          </div>
        </Panel>
      </div>
    </div>
  )
}

function Pill({ children }) {
  return (
    <span style={pill}>
      <span style={pillCheck}>✓</span>
      {children}
    </span>
  )
}

/// One $1-drift playbook card: a coloured scenario tag, the move, and what it does for you and the dollar.
function StratCard({ tone, tag, title, body }) {
  return (
    <div style={stratCard}>
      <div style={{ ...stratTag, color: tone }}>{tag}</div>
      <div style={stratTitle}>{title}</div>
      <div style={stratBody}>{body}</div>
    </div>
  )
}

// Greyed placeholder that fills the form column while hUSD is not deployed yet (Mining-page pattern).
function NotLiveBox({ note }) {
  return (
    <div style={{ minHeight: 240, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: 12, padding: 24 }}>
      <span style={{ width: 10, height: 10, borderRadius: '50%', background: T.accent }} />
      <div style={{ color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.body, lineHeight: 1.6, maxWidth: 260 }}>{note}</div>
    </div>
  )
}

// ─── styles (mirror SavingsPanel so the two halves match) ────────────────────
const banner = {
  display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderRadius: 12,
  background: T.accentDim, border: `1px solid ${T.border}`, color: T.textMuted,
  fontFamily: T.fontM, fontSize: T.fs.body, margin: '4px 0 24px',
}
const hero = { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }
const heroTitle = { color: T.text, fontFamily: T.fontD, fontSize: 38, fontWeight: 700, lineHeight: 1.05 }
const heroSub = {
  marginTop: 6, color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.body, lineHeight: 1.5, maxWidth: 560,
}
const intro = {
  marginTop: 8, marginBottom: 20, color: T.textMuted, fontFamily: T.fontM,
  fontSize: T.fs.body, lineHeight: 1.6, maxWidth: 720,
}
const grid = { display: 'grid', gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 1fr)', gap: 0 }
const leftCol = { padding: '32px 30px', minWidth: 0 }
const rightCol = {
  padding: '28px 26px', minWidth: 0, background: 'rgba(255,255,255,0.015)', borderLeft: `1px solid ${T.border}`,
}
const headline = { color: T.text, fontFamily: T.fontD, fontSize: 30, fontWeight: 700, lineHeight: 1.1 }
const pitch = {
  marginTop: 16, color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.body, lineHeight: 1.65, maxWidth: 440,
}
const pills = { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 22 }
const pill = {
  display: 'inline-flex', alignItems: 'center', gap: 8, padding: '7px 13px 7px 9px',
  borderRadius: 999, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
  color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.caption, whiteSpace: 'nowrap',
}
const pillCheck = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16,
  borderRadius: 999, background: 'rgba(91,158,255,0.18)', color: '#9CC6FF', fontSize: 10, fontWeight: 700,
}
const stratHead = {
  color: T.textDim, fontFamily: T.fontM, fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', marginBottom: 4,
}
const stratCard = {
  marginTop: 10, padding: '12px 15px', borderRadius: T.r.md,
  background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)',
}
const stratTag = { fontFamily: T.fontM, fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }
const stratTitle = { marginTop: 3, color: T.text, fontFamily: T.fontN, fontSize: T.fs.ui, fontWeight: 700 }
const stratBody = { marginTop: 5, color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.caption, lineHeight: 1.55 }
